import React, { useCallback, useEffect, useState } from 'react';
import DateTimePicker from '../../../components/DateTimePicker';
import SingleSelect from '../../../components/SingleSelect';
import { TanTable } from '../../../components/TanTable';
import service, { type AverageDaysResponse, type AverageDaysRow, type InvoiceClerk } from '../../../services/accountingReports.service';
import { avgDaysColumns } from './avgDaysColumns';
import { calendarDate, dateParam, reportError, REPORT_BUTTON } from './reportUtils';

const GROUPING = ['createdby'];
// Use the displayed, filtered members, so local table filtering also updates subtotals.
const clerkFooter = (value: unknown, _column: string, rows: AverageDaysRow[]) => {
  const total = rows.reduce((sum, row) => sum + (row.daysdiff ?? 0), 0);
  const average = rows.length ? Math.trunc(total / rows.length + 0.5) : 0;
  return `Average Days To Invoice (${String(value)}): ${average}`;
};
export default function AverageDaysToInvoiceReport({ embedded = false }: { embedded?: boolean }) {
  const Heading = embedded ? 'h2' : 'h1';
  const [start, setStart] = useState<Date | null>(() => new Date());
  const [end, setEnd] = useState<Date | null>(() => new Date());
  const [clerk, setClerk] = useState('');
  const [clerks, setClerks] = useState<InvoiceClerk[]>([]);
  const [clerkError, setClerkError] = useState<string | null>(null);
  const [clerkLoading, setClerkLoading] = useState(false);
  const [result, setResult] = useState<AverageDaysResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadClerks = useCallback(async () => {
    setClerkLoading(true); setClerkError(null);
    try { setClerks(await service.getInvoiceClerks()); }
    catch { setClerkError('Unable to load invoice clerks.'); }
    finally { setClerkLoading(false); }
  }, []);
  useEffect(() => { void loadClerks(); }, [loadClerks]);
  const run = async () => {
    if (!start || !end) { setError('Enter both a From Date and a To Date.'); return; }
    if (dateParam(start) > dateParam(end)) { setError('End date must be on or after start date.'); return; }
    setLoading(true); setError(null); setResult(null);
    try { setResult(await service.getAverageDaysToInvoice({ start: dateParam(start), end: dateParam(end), ...(clerk ? { clerk } : {}) })); }
    catch (e) { setError(reportError(e, 'Unable to load Average Days to Invoice. Please try again.')); }
    finally { setLoading(false); }
  };
  return <div className={`${embedded ? '' : 'p-4 '}space-y-4 text-label`}>
    <Heading className="text-xl font-semibold">Average Days to Invoice</Heading>
    <div className="rounded-lg border border-theme-border-primary bg-theme-bg-primary p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl">
        <DateTimePicker mode="date" labelType="custom" label="From Date" ariaLabel="From Date" required value={start} onChange={v => setStart(calendarDate(v))} disabled={loading} />
        <DateTimePicker mode="date" labelType="custom" label="To Date" ariaLabel="To Date" required value={end} onChange={v => setEnd(calendarDate(v))} disabled={loading} />
        <div><div className="text-sm mb-1">Invoice Clerk</div><SingleSelect ariaLabel="Invoice Clerk" value={clerk} onChange={setClerk} disabled={loading} loading={clerkLoading}
          options={[{ value: '', label: 'All Invoice Clerks' }, ...clerks.map(c => ({ value: c.username, label: c.fullName || c.username }))]} /></div>
      </div>
      {clerkError && <div className="flex gap-3 items-center text-sm"><p role="alert" className="text-red-600 dark:text-red-400">{clerkError}</p><button onClick={loadClerks} className="underline">Retry clerks</button></div>}
      <button className={REPORT_BUTTON} onClick={run} disabled={loading}>{loading ? 'Running…' : 'Run Report'}</button>
    </div>
    {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
    {result?.truncated && <p role="status" className="text-sm text-amber-700 dark:text-amber-300">Showing the first 50,000 rows. Averages cover these rows only. Narrow the date range or select an Invoice Clerk.</p>}
    {(result || loading) && <div className="h-[65vh] min-h-80">
      <TanTable<AverageDaysRow> settingsKey="accounting-average-days-to-invoice" cardTitle="Average Days to Invoice" data={result?.rows ?? []}
        columns={avgDaysColumns} loading={loading} enableGrouping groupableColumns={GROUPING} defaultGrouping={GROUPING} groupFooter={clerkFooter} fillContainer enableColumnFilters />
    </div>}
    {result && <p className="font-semibold">Overall Average: {result.overallAverage}</p>}
    <p className="text-sm text-muted">Days to Invoice is blank when a PRO has no delivery arrival. Those rows still count toward the averages above, which matches the V1 report.</p>
  </div>;
}
